import { Component, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MaterialModule } from '../../shared/material/material.module';
import { EnumDescriptionPipe } from '../../shared/pipes/enum-description.pipe';
import { ComparisonItem } from '../services/comparison.service';

interface ComparisonData {
  items: ComparisonItem[];
}

interface ComparisonField {
  label: string;
  key: string;
  values: any[];
  hasDifference: boolean;
  formatter?: (value: any) => string;
}

@Component({
  selector: 'app-comparison-dialog',
  standalone: true,
  imports: [CommonModule, MaterialModule, EnumDescriptionPipe],
  templateUrl: './comparison-dialog.component.html',
  styleUrls: ['./comparison-dialog.component.scss']
})
export class ComparisonDialogComponent {
  dialogRef = inject(MatDialogRef<ComparisonDialogComponent>);
  data = inject<ComparisonData>(MAT_DIALOG_DATA);
  
  items = computed(() => this.data.items);
  itemType = computed(() => this.data.items[0]?.type || 'service');
  
  // Computed para campos de comparação
  comparisonFields = computed(() => {
    const items = this.items();
    if (items.length === 0) return [];
    
    const type = this.itemType();
    
    if (type === 'service') {
      return this.getServiceComparisonFields(items);
    } else {
      return this.getResourceComparisonFields(items);
    }
  });
  
  private getServiceComparisonFields(items: ComparisonItem[]): ComparisonField[] {
    const fields: ComparisonField[] = [
      {
        label: 'ID',
        key: 'id',
        values: items.map(i => i.data.id),
        hasDifference: false
      },
      {
        label: 'Profissão',
        key: 'professionName',
        values: items.map(i => i.data.professionName),
        hasDifference: this.hasDifference(items.map(i => i.data.professionName))
      },
      {
        label: 'Especialidade',
        key: 'specialty',
        values: items.map(i => i.data.specialty),
        hasDifference: this.hasDifference(items.map(i => i.data.specialty))
      },
      {
        label: 'Áreas de Atuação',
        key: 'activityAreas',
        values: items.map(i => i.data.activityAreas),
        hasDifference: this.hasDifference(items.map(i => i.data.activityAreas))
      },
      {
        label: 'Atendimento Adulto',
        key: 'adultCare',
        values: items.map(i => i.data.adultCare ? 'Sim' : 'Não'),
        hasDifference: this.hasDifference(items.map(i => i.data.adultCare))
      },
      {
        label: 'Atendimento Pediátrico',
        key: 'pediatricCare',
        values: items.map(i => i.data.pediatricCare ? 'Sim' : 'Não'),
        hasDifference: this.hasDifference(items.map(i => i.data.pediatricCare))
      },
      {
        label: 'Formato Online',
        key: 'serviceFormatOnLine',
        values: items.map(i => i.data.serviceFormatOnLine ? 'Sim' : 'Não'),
        hasDifference: this.hasDifference(items.map(i => i.data.serviceFormatOnLine))
      },
      {
        label: 'Formato Presencial',
        key: 'serviceFormatPresential',
        values: items.map(i => i.data.serviceFormatPresential ? 'Sim' : 'Não'),
        hasDifference: this.hasDifference(items.map(i => i.data.serviceFormatPresential))
      },
      {
        label: 'Cidade',
        key: 'city',
        values: items.map(i => i.data.address?.city || '-'),
        hasDifference: this.hasDifference(items.map(i => i.data.address?.city))
      },
      {
        label: 'Estado',
        key: 'state',
        values: items.map(i => i.data.address?.state || '-'),
        hasDifference: this.hasDifference(items.map(i => i.data.address?.state))
      },
      {
        label: 'Valor',
        key: 'amount',
        values: items.map(i => i.data.amount || '-'),
        hasDifference: this.hasDifference(items.map(i => i.data.amount)),
        formatter: (value: any) => {
          if (value === '-') return value;
          return new Intl.NumberFormat('pt-BR', { 
            style: 'currency', 
            currency: 'BRL' 
          }).format(value);
        }
      },
      {
        label: 'Atendimento Plantonista',
        key: 'emergencyService',
        values: items.map(i => i.data.emergencyService ? 'Sim' : 'Não'),
        hasDifference: this.hasDifference(items.map(i => i.data.emergencyService))
      },
      {
        label: 'Status',
        key: 'status',
        values: items.map(i => i.data.status),
        hasDifference: this.hasDifference(items.map(i => i.data.status))
      }
    ];
    
    return fields;
  }
  
  private getResourceComparisonFields(items: ComparisonItem[]): ComparisonField[] {
    const fields: ComparisonField[] = [
      {
        label: 'ID',
        key: 'id',
        values: items.map(i => i.data.id),
        hasDifference: false
      },
      {
        label: 'Recursos',
        key: 'resource',
        values: items.map(i => this.formatResources(i.data.resource)),
        hasDifference: this.hasDifference(items.map(i => JSON.stringify(i.data.resource)))
      },
      {
        label: 'Cidade',
        key: 'city',
        values: items.map(i => i.data.address?.city || '-'),
        hasDifference: this.hasDifference(items.map(i => i.data.address?.city))
      },
      {
        label: 'Estado',
        key: 'state',
        values: items.map(i => i.data.address?.state || '-'),
        hasDifference: this.hasDifference(items.map(i => i.data.address?.state))
      },
      {
        label: 'Data de Criação',
        key: 'creationDate',
        values: items.map(i => i.data.creationDate),
        hasDifference: this.hasDifference(items.map(i => i.data.creationDate)),
        formatter: (value: any) => {
          if (!value) return '-';
          return new Date(value).toLocaleDateString('pt-BR');
        }
      },
      {
        label: 'Data de Publicação',
        key: 'publicationDate',
        values: items.map(i => i.data.publicationDate),
        hasDifference: this.hasDifference(items.map(i => i.data.publicationDate)),
        formatter: (value: any) => {
          if (!value) return '-';
          return new Date(value).toLocaleDateString('pt-BR');
        }
      },
      {
        label: 'Status',
        key: 'status',
        values: items.map(i => i.data.status),
        hasDifference: this.hasDifference(items.map(i => i.data.status))
      }
    ];
    
    return fields;
  }
  
  private hasDifference(values: any[]): boolean {
    if (values.length <= 1) return false;
    const first = JSON.stringify(values[0]);
    return values.some(v => JSON.stringify(v) !== first);
  }
  
  private formatResources(resources: string[] | undefined): string {
    if (!resources || resources.length === 0) return '-';
    return resources.join(', ');
  }
  
  formatValue(field: ComparisonField, index: number): string {
    const value = field.values[index];
    if (field.formatter) {
      return field.formatter(value);
    }
    return value?.toString() || '-';
  }
  
  close(): void {
    this.dialogRef.close();
  }
}
